/**
 * Fullscreen immersive Adhan overlay with synchronized text.
 *
 * Text synchronization is based on audio.currentTime (real-time position),
 * ensuring the displayed line matches the recitation regardless of reciter.
 * Falls back to timer if currentTime is unavailable.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X, Volume2, VolumeX, RotateCcw, MapPin, Share2, Check } from 'lucide-react';
import { Button } from '../ui/button';
import {
  getAdhanSyncTimeline, getLineAtTime, getAdhanDuration,
} from '../../data/adhanSyncData';
import { toast } from 'sonner';

const PRAYER_LABEL_FR = {
  Fajr: 'Fajr', Sunrise: 'Lever du soleil', Dhuhr: 'Ẓuhr',
  Asr: 'ʿAṣr', Maghrib: 'Maġrib', Isha: 'ʿIshāʾ',
};

const formatMs = (ms) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

export const AdhanFullscreen = ({
  open,
  prayerKey,        // 'Fajr', 'Dhuhr', …
  prayerAt,         // Date when the adhan started
  audioUrl,         // adhan sound URL to play
  soundId,          // sound identifier for timeline selection
  locationLabel,    // optional — city/country shown in the header
  onClose,
}) => {
  const [muted, setMuted] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [shareCopied, setShareCopied] = useState(false);
  const [useAudioTime, setUseAudioTime] = useState(false);
  const audioRef = useRef(null);
  const startTimeRef = useRef(null);
  const lastAudioTimeRef = useRef(null);

  const isFajr = prayerKey === 'Fajr';
  const timeline = useMemo(() => getAdhanSyncTimeline(soundId, isFajr), [soundId, isFajr]);
  const total = useMemo(() => getAdhanDuration(isFajr) * 1000, [isFajr]); // convert to ms
  const lineIdx = useAudioTime && audioRef.current
    ? getLineAtTime(timeline, audioRef.current.currentTime)
    : -1;
  const currentLine = lineIdx >= 0 ? timeline[lineIdx] : null;

  /* Audio-synced elapsed: prefer audio.currentTime, fallback to timer */
  useEffect(() => {
    if (!open) return undefined;
    
    const params = typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search) : null;
    const jumpToEnd = params?.get('adhan_demo_finish') === '1';

    if (jumpToEnd) {
      setElapsed(total);
      setUseAudioTime(false);
      return undefined;
    }

    // Set initial offset from prayerAt
    const offset = prayerAt
      ? Math.min(Math.max(Date.now() - prayerAt.getTime(), 0), total)
      : 0;
    startTimeRef.current = Date.now() - offset;
    setElapsed(offset);
    
    if (offset >= total) return undefined;

    // Attempt to sync with audio.currentTime if available
    let syncCheckId;
    const checkAudioSync = () => {
      if (!audioRef.current || audioRef.current.src === '') {
        setUseAudioTime(false);
        return;
      }
      
      // If audio is playing and has a valid currentTime, use it for sync
      const t = audioRef.current?.currentTime || 0;
      if (t > 0.5 && t !== lastAudioTimeRef.current) {
        setUseAudioTime(true);
        lastAudioTimeRef.current = t;
      } else if (t === 0 && !audioRef.current?.paused) {
        // Audio is loading/stalled
        setUseAudioTime(false);
      }
    };

    // Check for audio sync every 100ms initially
    syncCheckId = setInterval(checkAudioSync, 100);

    // Also use timer as fallback
    const timerId = setInterval(() => {
      if (!useAudioTime && startTimeRef.current) {
        const e = Date.now() - startTimeRef.current;
        setElapsed(e >= total ? total : e);
      }
    }, 250);

    return () => {
      clearInterval(syncCheckId);
      clearInterval(timerId);
    };
  }, [open, prayerAt, total, useAudioTime]);

  /* Continuous audio sync for line display */
  useEffect(() => {
    if (!useAudioTime || !audioRef.current) return undefined;
    
    const updateInterval = setInterval(() => {
      const t = audioRef.current?.currentTime || 0;
      lastAudioTimeRef.current = t;
    }, 100);
    
    return () => clearInterval(updateInterval);
  }, [useAudioTime]);

  /* Audio playback */
  useEffect(() => {
    if (!open) return undefined;
    if (!audioUrl) return undefined;
    const a = new Audio(audioUrl);
    a.muted = muted;
    a.preload = 'auto';
    audioRef.current = a;
    a.play().catch(() => { /* autoplay blocked — silent */ });
    return () => {
      try { a.pause(); a.src = ''; } catch { /* noop */ }
      audioRef.current = null;
    };
    // We re-init the audio on open + audioUrl, but mute is toggled via the
    // effect below (avoids ripping playback every time the user un-mutes).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, audioUrl]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.muted = muted;
  }, [muted]);

  /* Body scroll lock + Esc to exit */
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  const replay = () => {
    startTimeRef.current = Date.now();
    lastAudioTimeRef.current = null;
    setElapsed(0);
    setUseAudioTime(false);
    if (audioRef.current) {
      try {
        audioRef.current.currentTime = 0;
        audioRef.current.play().catch(() => {});
      } catch { /* noop */ }
    }
  };

  if (!open) return null;

  // Use audio time for elapsed if sync is active, otherwise use timer
  const displayElapsed = useAudioTime && audioRef.current
    ? audioRef.current.currentTime * 1000
    : elapsed;
  const remaining = Math.max(0, total - displayElapsed);
  const finished = displayElapsed >= total;

  /* Share — sends an invitation deep-link via the Web Share API
   * (Capacitor WebView, Android Chrome, iOS Safari) with a copy-to-clipboard
   * fallback so the user always gets the link even without share support. */
  const handleShare = async () => {
    // Always use production URL for sharing (never localhost)
    const prodUrl = 'https://noorelbayen.com';
    const url = `${prodUrl}/prieres/horaires?invite=${encodeURIComponent(prayerKey || 'prayer')}`;
    const text = locationLabel
      ? `L'Adhan de ${PRAYER_LABEL_FR[prayerKey] || prayerKey} vient d'être appelé à ${locationLabel}. Rejoins-moi pour la prière 🕌`
      : `L'Adhan de ${PRAYER_LABEL_FR[prayerKey] || prayerKey} vient d'être appelé. Rejoins-moi pour la prière 🕌`;
    try {
      if (typeof navigator !== 'undefined' && navigator.share) {
        await navigator.share({ title: 'Adhan — Noor El Bayen', text, url });
        return;
      }
    } catch {
      // user-cancelled or unsupported — fall through to clipboard
    }
    try {
      await navigator.clipboard.writeText(`${text}\n${url}`);
      setShareCopied(true);
      toast.success('Lien copié dans le presse-papier');
      setTimeout(() => setShareCopied(false), 2500);
    } catch {
      toast.error('Partage indisponible sur cet appareil');
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex flex-col bg-[#040814] text-text-primary
                 overflow-hidden adhan-fullscreen-root"
      style={{
        paddingTop: 'calc(env(safe-area-inset-top, 0px) + 16px)',
        paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 24px)',
      }}
      role="dialog"
      aria-label={`Adhan — ${PRAYER_LABEL_FR[prayerKey] || prayerKey}`}
      data-testid="adhan-fullscreen"
    >
      {/* Layered background: gradient + radial highlights + grain */}
      <div className="absolute inset-0 pointer-events-none -z-10">
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse at 50% 0%, rgba(212,175,55,0.25), transparent 60%),'
              + 'radial-gradient(ellipse at 50% 100%, rgba(6,64,43,0.35), transparent 60%),'
              + 'linear-gradient(180deg, #040814 0%, #0a0f22 50%, #040814 100%)',
          }}
        />
        {/* Soft floating orbs (CSS-only "particles") */}
        <div className="absolute -top-20 -left-20 w-96 h-96 rounded-full opacity-20 adhan-orb adhan-orb-1"
             style={{ background: 'radial-gradient(circle, #d4af37 0%, transparent 60%)' }} />
        <div className="absolute -bottom-32 -right-24 w-[28rem] h-[28rem] rounded-full opacity-15 adhan-orb adhan-orb-2"
             style={{ background: 'radial-gradient(circle, #06402b 0%, transparent 60%)' }} />
      </div>

      {/* Top bar */}
      <div className="relative z-10 flex items-center justify-between px-5">
        <button
          onClick={onClose}
          className="flex items-center gap-2 rounded-full bg-surface/70 backdrop-blur
                     border border-border px-3 py-2 text-sm
                     hover:bg-surface-elevated transition-colors"
          data-testid="adhan-fullscreen-exit"
          aria-label="Quitter l'Adhan"
        >
          <X className="w-4 h-4" />Quitter
        </button>

        <div className="text-center">
          <div className="text-[10px] uppercase tracking-[0.4em] text-text-muted">Adhan</div>
          <div className="font-serif text-base text-primary">
            {PRAYER_LABEL_FR[prayerKey] || prayerKey}
          </div>
        </div>

        <div className="flex items-center gap-1">
          <button
            onClick={() => setMuted((m) => !m)}
            className="rounded-full bg-surface/70 backdrop-blur border border-border p-2
                       hover:bg-surface-elevated transition-colors"
            data-testid="adhan-fullscreen-mute"
            aria-label={muted ? 'Réactiver le son' : 'Couper le son'}
          >
            {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
          </button>
          <button
            onClick={replay}
            className="rounded-full bg-surface/70 backdrop-blur border border-border p-2
                       hover:bg-surface-elevated transition-colors"
            data-testid="adhan-fullscreen-replay"
            aria-label="Rejouer l'Adhan"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Location pill */}
      {locationLabel && (
        <div className="relative z-10 mt-3 flex justify-center">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-surface/50 backdrop-blur
                          border border-border px-3 py-1 text-xs text-text-muted">
            <MapPin className="w-3 h-3" /> {locationLabel}
          </div>
        </div>
      )}

      {/* Main — current adhan line or end-of-adhan share card */}
      <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-6 text-center">
        {finished ? (
          <div className="text-center max-w-md mx-auto adhan-line-enter" data-testid="adhan-finished">
            <p
              className="font-serif text-primary"
              style={{ fontSize: 'clamp(1.6rem, 5vw, 2.4rem)' }}
            >
              L'Adhan vient d'être appelé
              {locationLabel ? <> à <span className="text-text-primary">{locationLabel}</span></> : null}
            </p>
            <p className="text-text-secondary text-sm sm:text-base mt-4 leading-relaxed">
              Que votre prière soit acceptée. Partagez ce moment avec un proche —
              une invitation à se recueillir ensemble.
            </p>

            <div className="mt-6 flex flex-col gap-2">
              <Button
                onClick={handleShare}
                className="bg-primary text-background hover:bg-primary-hover"
                data-testid="adhan-share-btn"
              >
                {shareCopied ? (
                  <><Check className="w-4 h-4 mr-2" />Lien copié</>
                ) : (
                  <><Share2 className="w-4 h-4 mr-2" />Partager une invitation à la prière</>
                )}
              </Button>
              <button
                onClick={replay}
                className="text-xs text-text-muted hover:text-primary transition-colors py-2"
                data-testid="adhan-replay-from-end"
              >
                Rejouer l'Adhan
              </button>
            </div>
          </div>
        ) : currentLine ? (
          <div
            key={lineIdx}
            className="max-w-2xl mx-auto adhan-line-enter"
            data-testid={`adhan-line-${lineIdx}`}
          >
            <p
              className="font-arabic leading-[1.7] text-text-primary"
              style={{
                fontFamily: '"Amiri", "Noto Naskh Arabic", serif',
                fontSize: 'clamp(2.2rem, 7vw, 3.4rem)',
              }}
              dir="rtl"
            >
              {currentLine.ar}
            </p>
            <p className="text-text-muted italic text-sm mt-3" dir="ltr">
              {currentLine.transliteration}
            </p>
            <p className="text-text-secondary text-base sm:text-lg mt-2 max-w-prose mx-auto">
              {currentLine.fr}
            </p>
          </div>
        ) : (
          <p className="text-text-muted text-sm">Préparation de l'Adhan…</p>
        )}

        {/* Line progression dots — hidden on the share screen */}
        {!finished && (
          <div className="mt-10 flex items-center gap-1.5" data-testid="adhan-progress-dots">
            {timeline.map((_, i) => (
              <span
                key={i}
                className={`block h-1.5 rounded-full transition-all duration-300 ${
                  i === lineIdx
                    ? 'w-6 bg-primary'
                    : i < lineIdx
                      ? 'w-1.5 bg-primary/60'
                      : 'w-1.5 bg-border'
                }`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Bottom — overall progress bar */}
      <div className="relative z-10 px-6">
        <div className="flex items-center justify-between text-[11px] font-mono text-text-muted">
          <span>{formatMs(displayElapsed)}</span>
          <span>-{formatMs(remaining)}</span>
        </div>
        <div className="h-1 mt-1 rounded-full bg-border/40 overflow-hidden">
          <div
            className="h-full bg-primary transition-[width] duration-300 ease-linear"
            style={{ width: `${Math.min(100, (elapsed / total) * 100)}%` }}
          />
        </div>
      </div>
    </div>
  );
};

export default AdhanFullscreen;
